import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
  type MRT_RowVirtualizer,
} from "material-react-table";
import { Box, Button, Typography } from "@mui/material";
import { JSX, useCallback, useRef, useState } from "react";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import { formatNumeral } from "../helpers/format";
import { useTableState } from "../stores/tableState";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { unparse } from "papaparse";
import { IconDownload } from "@tabler/icons-react";

interface VirtualizedTableProps<T extends Record<string, any>> {
  data: any;
  columns: MRT_ColumnDef<T>[];
  isLoading?: boolean;
  maxHeight?: string | number;
  fetchNextPage: () => Promise<any>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  initialState?: any;
  customRowActions?: (row: T, closeMenu: () => void) => JSX.Element[];
  showFilters?: boolean;
  totalCount?: number;
  onServerSort?: (field: string, desc: boolean) => void;
  disableSorting?: boolean;
  customHeader?: any;
  enableExportCSV?: boolean;
  enableExportPdf?: boolean;
}

export const VirtualizedTable = <T extends Record<string, any>>({
  data,
  columns,
  isLoading = false,
  maxHeight = "420px",
  fetchNextPage,
  initialState,
  customRowActions,
  isFetchingNextPage,
  showFilters,
  hasNextPage,
  totalCount,
  onServerSort,
  disableSorting,
  customHeader,
  enableExportCSV,
  enableExportPdf,
}: VirtualizedTableProps<T>) => {
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const { columnVisibility, setColumnVisibility, sorting, setSorting } =
    useTableState();

  const isLocked = useRef(false);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (
        containerRefElement &&
        !isFetchingNextPage &&
        !isLocked.current &&
        hasNextPage
      ) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        const atBottom = scrollHeight - scrollTop - clientHeight < 400;

        if (atBottom) {
          isLocked.current = true;
          fetchNextPage().finally(() => {
            setTimeout(() => {
              isLocked.current = false;
            }, 500);
          });
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage]
  );

  const getExportValue = (row: any, col: any): string => {
    if (typeof col.exportValue === "function") {
      return col.exportValue(row) || "";
    }

    if (typeof col.exportValue === "string") {
      // Suporte a paths tipo "customer.name"
      const keys = col.exportValue.split(".");
      return keys.reduce((acc: any, key: number) => acc?.[key], row) || "";
    }

    const value = row[col.accessorKey];
    return typeof value === "object"
      ? JSON.stringify(value)
      : value?.toString() || "";
  };

  const handleExportCSV = () => {
    if (!data?.length) return;

    const flattenedData = data.map((row: any) => {
      const obj: Record<string, string> = {};

      columns.forEach((col) => {
        if (!col.accessorKey) return;
        obj[col.header as string] = getExportValue(row, col);
      });

      return obj;
    });

    const csv = unparse(flattenedData, {
      delimiter: ";",
      quotes: true, // força aspas em todos os campos (mais seguro)
    });

    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "tabela.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPdf = () => {
    if (!data.length) return;

    const exportableColumns = columns
      .filter((col) => typeof col.accessorKey === "string")
      .map((col) => ({
        key: col.accessorKey!,
        header: col.header as string,
      }));

    const rows = data.map((row: any) =>
      exportableColumns.map((col) => {
        const columnDef = columns.find((c) => c.accessorKey === col.key);

        if (columnDef?.Cell) {
          const fakeCell = {
            getValue: () => row[col.key],
            row: { original: row },
            cell: { getValue: () => row[col.key] },
          };

          const rendered: any = columnDef.Cell(fakeCell as any);
          return typeof rendered === "string"
            ? rendered
            : typeof rendered === "number"
            ? rendered
            : (rendered?.props?.children || "").toString();
        }

        return row[col.key] ?? "";
      })
    );

    const headers = [exportableColumns.map((col) => col.header)];

    const doc = new jsPDF("l"); // "l" = landscape
    doc.setFontSize(10);
    doc.text("Relatório de Conciliação", 14, 14);

    autoTable(doc, {
      startY: 20,
      head: headers,
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
      margin: { top: 20 },
    });

    doc.save("tabela.pdf");
  };

  const table = useMaterialReactTable<T>({
    localization: MRT_Localization_PT_BR,
    columns,
    data,
    enableRowVirtualization: true,
    enableColumnVirtualization: true,
    rowVirtualizerInstanceRef,
    enableColumnResizing: true,
    layoutMode: "grid",
    rowVirtualizerOptions: { overscan: 5 },
    columnVirtualizerOptions: { overscan: 2 },
    enableGlobalFilter: true,
    enableColumnFilters: showFilters ?? false,
    enableColumnActions: showFilters ?? false,
    enableHiding: true,
    enableColumnOrdering: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enablePagination: false,
    enableSorting: !disableSorting,
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater) => {
      const updatedSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      setSorting(updatedSorting);

      if (onServerSort && updatedSorting?.[0]) {
        const { id, desc } = updatedSorting[0];
        onServerSort(id, desc ?? false);
      }
    },
    onColumnVisibilityChange: (updater) => {
      const updatedVisibility =
        typeof updater === "function"
          ? updater(table.getState().columnVisibility)
          : updater;
      setColumnVisibility(updatedVisibility);
    },
    state: {
      isLoading,
      sorting,
      rowSelection,
      columnVisibility,
      density: "compact",
    },
    muiSearchTextFieldProps: {
      sx: {
        minWidth: "350px",
        maxWidth: "100%",
        mt: 0,
        "&.MuiInputBase-root": {
          mt: "0px !important",
        },
        "&.MuiOutlinedInput-root": {
          mt: "0px !important",
          backgroundColor: "#282828",
          border: "none",
        },
      },
      InputProps: {
        sx: {
          mt: "0px !important",
          backgroundColor: "transparent !important",
          border: "none !important",
          boxShadow: "none !important",
        },
      },
      inputProps: {
        sx: {
          backgroundColor: "transparent !important",
          border: "none !important",
          paddingLeft: "0 !important",
          paddingRight: "0 !important",
          paddingTop: "4px !important",
          paddingBottom: "4px !important",
          borderRadius: "0 !important",
          boxShadow: "none !important",
        },
      },
    },
    initialState: {
      ...initialState,
      sorting,
      columnVisibility,
      showGlobalFilter: true,
      showColumnFilters: true,
    },
    enableRowNumbers: false,
    enableRowActions: !!customRowActions,
    ...(customRowActions && {
      renderRowActionMenuItems: ({ row, closeMenu }) =>
        customRowActions(row.original, closeMenu),
    }),

    renderTopToolbarCustomActions: () => {
      return (
        <Box
          sx={{
            px: 2,
            gap: "2rem",
            display: "flex",
            alignItems: "center",
            mt: 1,
          }}
        >
          {customHeader}
          {enableExportCSV && (
            <Button
              onClick={() => handleExportCSV()}
              startIcon={<IconDownload />}
              size="small"
            >
              exportar CSV
            </Button>
          )}
          {enableExportPdf && (
            <Button
              onClick={() => handleExportPdf()}
              startIcon={<IconDownload />}
              size="small"
            >
              exportar PDF
            </Button>
          )}
        </Box>
      );
    },
    renderBottomToolbarCustomActions: () => (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
        {totalCount
          ? `Exibindo ${formatNumeral(data.length)} de ${formatNumeral(
              totalCount
            )} linhas`
          : `Total de linhas: ${formatNumeral(data.length)}`}
      </Typography>
    ),
    muiTableContainerProps: {
      sx: (theme) => ({
        maxHeight,
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
        borderRadius: 2,
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.secondary.light,
          borderRadius: 6,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: theme.palette.secondary.dark,
        },
      }),
      onScroll: (e) => fetchMoreOnBottomReached(e.currentTarget),
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: (theme) => ({
        boxShadow: "none",
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
        border: `1px solid ${theme.palette.secondary.light}`,
        borderRadius: 2,
      }),
    },

    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        setSelectedRowId((prev) => (prev === row.id ? null : row.id));
      },
      sx: (theme) => ({
        cursor: "pointer",
        backgroundColor:
          selectedRowId === row.id
            ? theme.palette.secondary.light
            : theme.palette.secondary[
                "100" as keyof typeof theme.palette.secondary
              ],
        "&:hover": {
          backgroundColor: theme.palette.secondary.light,
        },
      }),
    }),
    muiTableHeadCellProps: {
      sx: (theme) => ({
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
        color: "#FFF",
        fontWeight: 700,
        fontSize: "14px",
        borderBottomColor: theme.palette.secondary.light,
        borderBottomWidth: 1,
        borderBottomStyle: "solid",
        overflow: "visible",
      }),
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
      }),
    },

    muiTopToolbarProps: {
      sx: (theme) => ({
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
        borderBottom: (theme) => `1px solid ${theme.palette.secondary.light}`,
        "& > .MuiTextField-root": {
          mt: "0px !important",
        },
      }),
    },
    muiTableBodyProps: {
      sx: (theme) => ({
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
        '&[data-column-id="mrt-row-actions"]': {
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
        },
      }),
    },
    muiBottomToolbarProps: {
      sx: (theme) => ({
        backgroundColor:
          theme.palette.secondary[
            "100" as keyof typeof theme.palette.secondary
          ],
        display: "flex",
        justifyContent: "space-between",
        px: 2,
      }),
    },
    enableDensityToggle: false,
    enableColumnPinning: true,
    enableStickyFooter: true,
  });

  return <MaterialReactTable table={table} />;
};

export default VirtualizedTable;
